/**
 * The backtick key (`) opens Owner tools over the page the owner is on, from
 * anywhere in the app (Idea #82; ADR 0098). The owner asked for the tools
 * hidden by default, "a key combo to reveal", and then for "a popup menu that
 * if clicked outside of, or pressed the backtick key again, would disappear
 * and leave me where I was" (#512; ADR 0134).
 *
 * - **Owners only.** For a tech the key does nothing, and /tools refuses them
 *   (RequireOwner). The API refuses them in its service layer either way.
 * - **Never while typing:** in a text field, a text area, a select or anything
 *   editable, the key types as usual, in the popup's own fields too. Nor with
 *   Ctrl, Alt or the Command key, which belong to the browser and the system.
 * - **Closed by** the key again, Escape, its Close button or a click outside,
 *   and by going to another page from one of its links. Focus goes back where
 *   it was. Escape in an open list closes only the list.
 * - **Not on /tools,** where the tools are the page itself.
 * - **Also from the user menu** (OwnerToolsOpener): on a phone, a backtick is
 *   hard to type with no keyboard open (the owner, 2026-10-15).
 * - **The page behind doesn't scroll** while it's open (DialogBackdrop; ADR
 *   0191).
 */
import * as Dialog from "@radix-ui/react-dialog";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { DialogBackdrop } from "../components/ui/DialogBackdrop";
import { isOpenList, isTypingIn } from "../lib/keys";
import { OwnerTools } from "../pages/tools/OwnerToolsPage";
import { OwnerToolsOpener } from "./owner-tools-context";
import { useSession } from "./session-context";

const OWNER_TOOLS_PATH = "/tools";
const OWNER_TOOLS_KEY = "`";

export function OwnerToolsKey({ children }: { children?: ReactNode }) {
  const { me } = useSession();
  const location = useLocation();
  const isOwner = me.role === "owner";
  const onToolsPage = location.pathname === OWNER_TOOLS_PATH;
  // Open on the page it was opened on: going elsewhere, by one of its links
  // or the browser's Back, closes it.
  const [openOn, setOpenOn] = useState<string | null>(null);
  const open = isOwner && openOn === location.key;
  // Where focus was when it opened, to go back to on closing.
  const returnTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOwner || onToolsPage) {
      return;
    }
    function onKeyDown(event: KeyboardEvent) {
      if (
        event.key !== OWNER_TOOLS_KEY ||
        event.ctrlKey ||
        event.altKey ||
        event.metaKey ||
        event.repeat ||
        event.isComposing ||
        event.defaultPrevented ||
        isTypingIn(event.target)
      ) {
        return;
      }
      event.preventDefault();
      if (openOn === location.key) {
        setOpenOn(null);
        return;
      }
      returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setOpenOn(location.key);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOwner, onToolsPage, openOn, location.key]);

  const openFromMenu = useCallback(
    (focusAfter: HTMLElement | null) => {
      returnTo.current = focusAfter;
      setOpenOn(location.key);
    },
    [location.key],
  );

  return (
    <OwnerToolsOpener.Provider value={isOwner && !onToolsPage ? openFromMenu : null}>
      {children}
      <Dialog.Root
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setOpenOn(null);
          }
        }}
      >
        <Dialog.Portal>
          {/* Ours, not Radix's Overlay, whose scroll lock the CSP refuses (ADR 0191). */}
          <DialogBackdrop data-testid="owner-tools-backdrop" className="fixed inset-0 z-40 bg-black/45" />
          <Dialog.Content
            className={
              "fixed top-4 left-1/2 z-50 flex max-h-[calc(100vh-2rem)] w-[min(760px,calc(100vw-2rem))] " +
              "-translate-x-1/2 flex-col gap-3 overflow-y-auto rounded-panel border border-line bg-page p-4 " +
              "text-text shadow-[0_14px_30px_var(--brand-shadow)] min-[700px]:top-[6vh] min-[700px]:max-h-[88vh]"
            }
            onEscapeKeyDown={(event) => {
              if (isOpenList(event.target)) {
                // The list closes itself; the popup stays.
                event.preventDefault();
              }
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              if (returnTo.current?.isConnected) {
                returnTo.current.focus();
              }
              returnTo.current = null;
            }}
          >
            <div className="flex items-start justify-between gap-3 px-1">
              <div className="flex flex-col gap-1">
                <Dialog.Title className="text-xl font-semibold tracking-tight">Owner tools</Dialog.Title>
                <Dialog.Description className="text-[13.5px] text-muted">
                  The worker&apos;s jobs, started now. The backtick key again, Escape or a click outside closes
                  this, leaving you where you were.
                </Dialog.Description>
              </div>
              <Dialog.Close
                aria-label="Close Owner tools"
                className={
                  "flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-pill border border-line " +
                  "bg-panel text-lg leading-none hover:bg-panel-2 focus-visible:outline-2 " +
                  "focus-visible:outline-offset-2 focus-visible:outline-brand-ink"
                }
              >
                <span aria-hidden="true">×</span>
              </Dialog.Close>
            </div>
            <OwnerTools inPopup />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </OwnerToolsOpener.Provider>
  );
}
